import ko from 'knockout';
import _ from 'underscore';
import uuid from '@bingads-webui-component-legacy/uuid/index';
import * as schemaMapper from '@bingads-webui-campaign-legacy/crud-ad/schemaMapper';
import { MMAAssetConfigs } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');
const MAX_HEADLINES = MMAAssetConfigs.MAX_HEADLINES;
const MAX_LONGHEADLINES = MMAAssetConfigs.MAX_LONGHEADLINES;
const MAX_DESCRIPTIONS = MMAAssetConfigs.MAX_DESCRIPTIONS;

export default class {
  constructor({
    idField = 'Id',
    maxAdsCount = 50,
    adGroupId = '',
    adGroups = [],
    isMixedModeCampaignCreation = false,
  } = {}) {
    this.idField = idField;
    this.maxAdsCount = maxAdsCount;
    this.adGroupId = adGroupId;
    this.isMixedModeCampaignCreation = isMixedModeCampaignCreation;

    this.adGroup = ko.observable();
    this.externalCachedImageExtensionChanges = ko.observable();
    this.firstTwoKeywords = ko.observableArray([]);
    this.allAdGroupsInfo = ko.observableArray([]);
    this.hasMultiAdgroups = ko.computed(() => {
      return this.allAdGroupsInfo().length > 1;
    });

    this.defaultAds = _.constant([
      { action: 'create' },
      { action: 'dash' },
    ]);

    this.ads = _.clone(this.defaultAds());

    this.deletedAdIds = [];

    this.alertMessage = ko.observable();
    this.duplicateAlertMessage = ko.observable();

    this.refresh(adGroups);
  }

  getAds() {
    return this.ads.slice(0, -2);
  }

  //when back to adgroup step, then go next to this step, need to refresh adgroup name and keywords list
  refresh(adGroups) {
    const adGroup = _.find(adGroups, { [this.idField]: this.adGroupId });
    let allAdGroups = adGroups;

    this.adGroup(adGroup);
    // for mixed mode campaign creation
    // only ad groups of the same type should be displayed in the "copy to ad group" menu
    if (this.isMixedModeCampaignCreation) {
      allAdGroups = _.filter(adGroups, (adg) => adg.AdGroupType === adGroup.AdGroupType);
    }

    this.allAdGroupsInfo(_.map(allAdGroups, ({ Name, [this.idField]: id }) => {
      return {
        Name,
        [this.idField]: id,
      };
    }));

    this._generateKeywordInfo();

    if (!this.isEmpty()) {
      this.slb.refreshSource();
    }
  }

  _generateKeywordInfo() {
    const Two = 2;
    const keywords = _.pluck(this.adGroup().Keywords, 'Text');

    this.firstTwoKeywords(_.first(keywords, Two));

    if (_.size(this.adGroup().Keywords) > Two) {
      this.firstTwoKeywords.push(i18n.get('Ad_MoreKeywords', {
        length: this.adGroup().Keywords.length - Two,
      }));
    }
  }

  isEmpty() {
    return this.ads.length === this.defaultAds().length;
  }

  totalCounts() {
    return this.ads.length - this.defaultAds().length;
  }

  isFull() {
    return this.ads.length === this.defaultAds().length + this.maxAdsCount;
  }

  _pushAdToArray(ad) {
    this.ads.splice(this.ads.length - 2, 0, ad);
  }

  onDelete(ad) {
    const index = _.findIndex(this.ads, {
      [this.idField]: ad[this.idField],
    });

    this.ads.splice(index, 1);
    this.slb.refreshSource();

    if (ad.isSaved) {
      // this ad had been saved to MT server.
      this.deletedAdIds.push({ Id: ad[this.idField], Type: ad.Type });
    }

    if (this.isEmpty()) {
      this.externalCachedImageExtensionChanges(null);
    }

    //clear reach maxLength alert message
    this.alertMessage('');

    this.refreshDuplicateMessage();
  }

  refreshDuplicateMessage() {
    const hasDuplicate = _.some(this.ads, (entity) => {
      return entity.isDuplicated;
    });

    this.duplicateAlertMessage(hasDuplicate ?
      i18n.get('AdGroup_Validation_DuplicateAds_AlertMessage') : '');
  }

  updateMMAfields(ad) {
    if (!_.isEmpty(ad.Headlines) && typeof ad.Headlines === 'string') {
      ad.Headlines = JSON.parse(ad.Headlines);
      ad.Headline = _.first(ad.Headlines).Text;
      _.each(ad.Headlines, (val, key) => {
        ad[`Headline${key}`] = val.Text;
      });
      for (let i = ad.Headlines.length; i < MAX_HEADLINES; i++) {
        delete ad[`Headline${i}`];
      }
    }
    if (!_.isEmpty(ad.LongHeadlines) && typeof ad.LongHeadlines === 'string') {
      ad.LongHeadlines = JSON.parse(ad.LongHeadlines);
      ad.LongHeadline = _.first(ad.LongHeadlines).Text;
      _.each(ad.LongHeadlines, (val, key) => {
        ad[`LongHeadline${key}`] = val.Text;
      });
      for (let i = ad.LongHeadlines.length; i < MAX_LONGHEADLINES; i++) {
        delete ad[`LongHeadline${i}`];
      }
    }
    if (!_.isEmpty(ad.Descriptions) && typeof ad.Descriptions === 'string') {
      ad.Descriptions = JSON.parse(ad.Descriptions);
      ad.Text = _.first(ad.Descriptions).Text;
      _.each(ad.Descriptions, (val, key) => {
        ad[`Description${key}`] = val.Text;
      });
      for (let i = ad.Descriptions.length; i < MAX_DESCRIPTIONS; i++) {
        delete ad[`Description${i}`];
      }
    }
  }

  syncTheExtension(changes) {
    this.externalCachedImageExtensionChanges(changes);
  }

  onUpdate(newAd, originalAd) {
    this.updateMMAfields(newAd);
    const index = _.findIndex(this.ads, {
      [this.idField]: originalAd[this.idField],
    });

    if (!originalAd.isSaved && this.checkIfExist(newAd)) {
      this.ads.splice(index, 1);
    } else {
      this.ads[index] = newAd;
    }

    this.slb.refreshSource();

    this.refreshDuplicateMessage();
  }

  onCreate(ad) {
    this.updateMMAfields(ad);
    if (this.checkIfExist(ad)) {
      return;
    }

    this._pushAdToArray(ad);
    this.slb.refreshSource();

    //clear empty adgroup alert message
    this.alertMessage('');
  }

  onCopy(ad) {
    const copiedAd = _.clone(ad);

    copiedAd.isDuplicated = false;
    copiedAd[this.idField] = uuid.v4();
    copiedAd.isSaved = false;

    this._pushAdToArray(copiedAd);
    this.slb.refreshSource();
  }

  checkIfExist(ad) {
    const savedAdsIds = _.pluck(_.where(this.ads, { isSaved: true } ), 'Id');

    return _.contains(savedAdsIds, ad.Id);
  }

  changes() {
    return {
      addedAds: _.chain(this.ads)
        .filter((ad) => {
          return !ad.isSaved && ad.Type;
        })
        .map((ad) => {
          ad.AdGroup = { Id: this.adGroupId };
          return ad;
        })
        .value(),
      removedAdIds: _.map(this.deletedAdIds, (deleted) => {
        return _.defaults(deleted, { AdGroup: { Id: this.adGroupId } });
      }),
    };
  }

  hasDuplicateAds() {
    //TODO [lizho] refacter to be more efficient. The ads array can't be sorted firstly, otherwise the panel will be different.
    _.each(this.ads, (ad) => {
      ad.isDuplicated = false;
    });

    let hasDuplicate = false;

    for (let index = 0; index < this.totalCounts(); index++) {
      if (this.ads[index].isDuplicated) {
        continue; // eslint-disable-line
      }

      const sourceAd = _.pick(this.ads[index], schemaMapper[this.ads[index].Type]());

      for (let j = index + 1; j < this.totalCounts(); j++) {
        if (_.isEqual(sourceAd, _.pick(this.ads[j], schemaMapper[this.ads[j].Type]()))) {
          this.ads[j].isDuplicated = true;
          hasDuplicate = true;
        }
      }
    }

    this.slb.refreshSource();
    this.refreshDuplicateMessage();

    return hasDuplicate;
  }

  getCachedImageExtensionChanges() {
    return this.externalCachedImageExtensionChanges();
  }

  findAd(id) {
    return _.find(this.ads, { [this.idField]: id });
  }
}
